import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SolihullCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Solihull Pothole Claims | 1,640 Repairs, 27% U-Roads RED | Fixtyer"
        description="Solihull filled 1,640 potholes in 2024/25 — up 118% in three years — while 27% of its 680km unclassified network is in RED condition. HS2 diversions are accelerating rural road decay. See what the DfT AMBER rating means for your claim."
        canonical="/councils/solihull"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Solihull', url: '/councils/solihull' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Solihull has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Solihull matches its £7.162m capital allocation in 2025/26 and projects 81% preventative maintenance. But Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate budgets. The overall DfT rating is AMBER because road condition and best practice are both AMBER.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 680km — 69% of Solihull\'s 985km carriageway network. The council\'s own transparency report shows 27% of U-roads in RED condition in 2024, up from 17% in 2021. The council admits HS2 diversion routes have accelerated deterioration on rural U-roads, where only "make safe" repairs could be undertaken until construction works finish.',
          },
          {
            question: 'Does the council\'s claim that insurance claims have reduced weaken my case?',
            answer: 'Not necessarily. Solihull states that its preventative approach "has reduced" insurance claims — a borough-wide trend, not proof that your specific defect was reasonably managed. Pothole repairs still rose from 753 in 2021/22 to 1,640 in 2024/25, and the council documents faster-than-expected deterioration on diversion routes. Your claim lives on evidence about your pothole, not borough-wide claim statistics.',
          },
          {
            question: 'Does the HS2 diversion admission help my claim?',
            answer: 'Yes. Solihull publicly states that rural unclassified roads used as HS2 diversion routes have deteriorated faster than expected, that traffic management conflicts prevented major repairs, and that a "make safe" approach was required. That is documented knowledge of elevated risk on specific corridors — which raises the bar for what a reasonable inspection and repair regime looks like on affected routes under Section 58.',
          },
          {
            question: 'Pothole repairs more than doubled — does that mean the roads are fixed?',
            answer: 'No. Solihull filled 1,640 potholes in 2024/25 compared with 753 in 2021/22 — a 118% rise. The council\'s own report describes a "steady state" on classified roads while U-road RED condition climbed back to 27%. Rising reactive repair counts on a network where 69% is unclassified roads is evidence that defects keep forming — exactly the scenario where prior reports and photographic evidence decide claims.',
          },
          {
            question: 'Who maintains the roads around Birmingham Airport and the NEC?',
            answer: 'Solihull Council maintains the borough\'s 985km local road network, including routes serving Birmingham Airport, Birmingham International Station and the NEC — all within the borough boundary. National Highways maintains trunk roads and motorways. HS2 Interchange Station will also sit within Solihull. Check who maintains your specific road before claiming; defects on trunk roads go to National Highways, not the borough council.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Solihull Metropolitan Borough Council"
        councilSlug="solihull"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/solihull"
        headline="Solihull Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Solihull</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Solihull: pothole repairs more than doubled as HS2 diversions tore through rural roads
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Solihull earns a <strong className="text-emerald-400">GREEN</strong> DfT spend scorecard and
            projects 81% preventative maintenance for 2025/26. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because pothole repairs climbed from{' '}
            <strong className="text-rose-400">753 to 1,640 in three years</strong>, 27% of the borough&apos;s
            680km unclassified network is in RED condition, and the council admits HS2 diversion traffic is
            accelerating decay on rural roads it can only &ldquo;make safe&rdquo;.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">1,640</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 753 in 2021/22 — a 118% rise on a 985km network where unclassified roads account
              for 69% of carriageway length and 27% were in RED condition at the last survey.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* DfT scorecard cards */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the DfT AMBER rating means — and what it doesn&apos;t
          </h2>
          <p className="text-surface-600 mb-8">
            Three scorecards from the Department for Transport&apos;s 2025/26 local road maintenance ratings,
            cross-checked against Solihull&apos;s own June 2025 transparency report
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <RatingBadge rating="amber" size="md" />
                <span className="text-sm font-semibold text-amber-900">Condition</span>
              </div>
              <div className="text-3xl font-bold text-amber-600 mb-2">27%</div>
              <div className="text-sm text-amber-900 font-medium">U-roads in RED (2024)</div>
              <div className="text-xs text-surface-500 mt-2">
                Classified A and B/C roads perform well — 3% and 2% RED respectively in 2024. But
                680km of unclassified roads carry the borough&apos;s AMBER condition mark.
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <RatingBadge rating="green" size="md" />
                <span className="text-sm font-semibold text-emerald-900">Spend</span>
              </div>
              <div className="text-3xl font-bold text-emerald-600 mb-2">£7.16m</div>
              <div className="text-sm text-emerald-900 font-medium">DfT capital allocation 2025/26</div>
              <div className="text-xs text-surface-500 mt-2">
                Solihull projects spending its full £7.162m allocation with 81% on preventative
                maintenance — up from 67% reactive-heavy spending in 2023/24.
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <RatingBadge rating="amber" size="md" />
                <span className="text-sm font-semibold text-amber-900">Best practice</span>
              </div>
              <div className="text-3xl font-bold text-amber-600 mb-2">PAS 2161</div>
              <div className="text-sm text-amber-900 font-medium">Methodology change from 2026/27</div>
              <div className="text-xs text-surface-500 mt-2">
                Solihull has asset management plans and SCANNER surveys, but the DfT&apos;s AMBER best
                practice scorecard signals room for improvement in documented processes and reporting.
              </div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The honest read</h3>
            <p className="text-brand-200 mb-4">
              Solihull is not a failing council. It matches its DfT allocation, runs a risk-based
              prioritisation programme (A roads first, then B, C, then other roads), and its classified
              road condition is genuinely strong. A generic template claim on an A-road near the airport
              will face a well-organised Section 58 defence.
            </p>
            <p className="text-white font-medium">
              But the DfT rates programmes, not potholes. Solihull&apos;s own data shows reactive repairs
              surging, U-road RED condition climbing, and HS2-related diversions damaging rural roads
              faster than major repairs could be scheduled. The rating doesn&apos;t decide your claim — the
              evidence about <em>your</em> defect does.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Solihull&apos;s own transparency report — classified roads steady,
            unclassified roads slipping
          </p>

          {/* Network overview */}
          <div className="grid sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-brand-900">985km</div>
              <div className="text-xs text-surface-600 mt-1">Total carriageway</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-brand-900">120km</div>
              <div className="text-xs text-surface-600 mt-1">A roads (12%)</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-brand-900">185km</div>
              <div className="text-xs text-surface-600 mt-1">B and C roads (19%)</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">680km</div>
              <div className="text-xs text-amber-800 mt-1">U roads (69%)</div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (120km): target 4% RED, currently 3%</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">67%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">65%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center">30%</td>
                    <td className="px-4 py-2 text-center">64%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">3%</td>
                    <td className="px-4 py-2 text-center font-bold">29%</td>
                    <td className="px-4 py-2 text-center font-bold">68%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: A-roads are back below Solihull&apos;s 4% RED target after a spike to 6% in
              2023, when resources were diverted to B and C road repairs. But A-roads are just 12% of
              the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (185km): broadly stable</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">88%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">80%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">82%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center text-amber-600">23%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">2%</td>
                    <td className="px-4 py-2 text-center font-bold">14%</td>
                    <td className="px-4 py-2 text-center font-bold">84%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B and C roads recovered after the 2023 spike — 84% Green in 2024. The council notes it
              redirected resources to these roads that year, which temporarily pushed A-road RED above target.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (680km — 69% of network): deteriorating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">36%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">17%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">21%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">21%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">27%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              After improving from 36% RED in 2020 to 17% in 2021, U-road condition has climbed back to
              27% — roughly 184km of residential streets, estate roads and rural routes that the council
              considers in need of maintenance treatment.
            </p>
            <p className="text-amber-800 text-sm mt-3">
              U-roads are assessed by visual inspection rather than SCANNER laser surveys. The council
              has introduced VAISALA continual surveys, but the published RED percentages above come
              from the visual inspection programme.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend, AMBER condition</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.16m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">81%</div>
                <div className="text-brand-300 text-sm">Projected preventative share 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£1.37bn</div>
                <div className="text-brand-300 text-sm">Estimated network replacement value</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Solihull spends its full DfT allocation and has shifted back toward prevention — yet
              pothole repairs hit a five-year high and U-road RED condition is rising. The problem on
              residential and rural roads is not the chequebook alone; it is diversion traffic, repair
              scheduling conflicts, and a network where defects outpace inspections.
            </p>
          </div>
        </div>
      </section>

      {/* The HS2 diversion admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The HS2 diversion admission
          </h2>
          <p className="text-surface-600 mb-8">
            The council&apos;s own explanation for U-road deterioration — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The unclassified network has deteriorated due to a reduction in available funding and the increased use of several rural roads as diversion routes due to HS2. As these are being used as diversion routes only minor maintenance can be undertaken which does not prevent further deterioration from occurring."
              source="Solihull Metropolitan Borough Council — Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard
              quote="Whilst the impact has been seen on the Classified network the unclassified rural roads have suffered the most deterioration as a result. This is often due to the traffic management conflicts meaning it has been difficult to undertake major repairs until the works have been completed. As a result a 'make safe' repairs approach has had to be undertaken to keep the road safe."
              source="Solihull Metropolitan Borough Council — Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Solihull formally acknowledges that HS2-related diversion traffic is accelerating
                deterioration on rural unclassified roads — and that major repairs could not be scheduled
                because of traffic management conflicts. That is documented knowledge of elevated risk on
                specific corridors.
              </p>
              <p className="text-surface-600 text-sm">
                Knowledge of accelerated wear raises the standard for what a &ldquo;reasonable&rdquo; inspection
                frequency looks like on affected routes under Section 58.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road on or near an HS2 diversion route?</li>
                <li>• Did the council increase inspection frequency on those corridors?</li>
                <li>• If deterioration was known to be accelerating, why was only a &ldquo;make safe&rdquo; patch applied?</li>
                <li>• Was your pothole on one of the 88 roads scheduled for 2025/26 resurfacing — or still waiting?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4,233 potholes in four years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair counts from Solihull&apos;s transparency report — undertaken in line with the
            DfT Code of Practice for well-maintained highways
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">753</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">775</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">1,065</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,640</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,233</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-xs mb-8">
            The council&apos;s published table lists five column headers including &ldquo;2022/22&rdquo; (apparent
            typographical error) with values 753, 773, 775, 1,065 and 1,640. The four financial years
            above use the 2021/22, 2022/23, 2023/24 and 2024/25 figures (753, 775, 1,065, 1,640).
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">More than doubled in three years</h3>
              <p className="text-surface-600 text-sm">
                Pothole repairs rose 118% from 753 in 2021/22 to 1,640 in 2024/25 — an average of
                roughly 4.5 potholes filled every day in the most recent year. A network producing
                defects at that accelerating rate is one where potholes routinely form between
                inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Against a &ldquo;steady state&rdquo; narrative</h3>
              <p className="text-amber-800 text-sm">
                The council states that &ldquo;a steady state is being maintained for the condition of the
                highway network&rdquo; — but that characterisation fits classified roads, not the U-road
                network where RED condition has climbed from 17% to 27% while reactive repairs surged.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending table */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Maintenance spending — shifting back to prevention
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of capital and revenue spend from Solihull&apos;s transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">4,278</td>
                  <td className="px-4 py-3 text-center">4,336</td>
                  <td className="px-4 py-3 text-center">2,567</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">5,733</td>
                  <td className="px-4 py-3 text-center">5,733</td>
                  <td className="px-4 py-3 text-center">2,686</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">5,737</td>
                  <td className="px-4 py-3 text-center">6,417</td>
                  <td className="px-4 py-3 text-center">2,657</td>
                  <td className="px-4 py-3 text-center">87%</td>
                  <td className="px-4 py-3 text-center">13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">4,146</td>
                  <td className="px-4 py-3 text-center">4,293</td>
                  <td className="px-4 py-3 text-center">2,723</td>
                  <td className="px-4 py-3 text-center text-rose-600">67%</td>
                  <td className="px-4 py-3 text-center text-rose-600">33%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">4,552</td>
                  <td className="px-4 py-3 text-center">4,552</td>
                  <td className="px-4 py-3 text-center">2,666</td>
                  <td className="px-4 py-3 text-center">78%</td>
                  <td className="px-4 py-3 text-center">22%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">7,162</td>
                  <td className="px-4 py-3 text-center font-bold">7,162</td>
                  <td className="px-4 py-3 text-center">2,630</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">81%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">2025/26 resurfacing programme</h3>
            <p className="text-surface-600 text-sm">
              For 2025/26, Solihull has identified 88 roads for resurfacing covering an estimated 35km,
              developed using in-car AI camera defect surveys plus inspector input. The council&apos;s stated
              aim is to &ldquo;reduce the number of potholes appearing&rdquo; — but 1,640 were filled in the
              year before this programme begins.
            </p>
          </div>
        </div>
      </section>

      {/* What this means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a well-funded AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Solihull invests seriously on classified roads — here&apos;s how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — matches full £7.162m DfT allocation for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 81% projected preventative maintenance — up from a reactive-heavy 2023/24
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads at 3% RED — below the council&apos;s 4% target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management plans and permit scheme for streetworks
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Council states insurance claims have reduced under its preventative approach
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on principal A and B roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 27% of U-roads in RED at the 2024 survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 680km unclassified network — 69% of the borough where claims most often arise
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pothole repairs up 118% in three years — defects forming faster than prevention catches them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted HS2 diversion damage with only &ldquo;make safe&rdquo; repairs possible
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice scorecard — documented process gaps the DfT has flagged
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a preventative maintenance narrative, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online form) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the 27% RED rate and HS2 diversion admission are your strongest structural arguments</li>
              <li>• Location relative to documented HS2 diversion routes and traffic management conflicts</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Solihull&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Solihull first
          </h2>
          <p className="text-surface-600 mb-6">
            Solihull&apos;s transparency report describes reactive repairs organised by highway inspectors
            in line with the DfT Code of Practice. Reporting the defect through the council creates a dated
            record — useful evidence if the pothole was reported before your incident, or if the council failed
            to repair it within a reasonable time.
          </p>
          <a
            href="https://www.solihull.gov.uk/roads-pavements-and-streetcare/potholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Solihull Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the council&apos;s online form. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Solihull?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded borough with HS2 pressure on rural roads demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road deterioration argument</li>
                <li>• No prior-report search</li>
                <li>• No HS2 diversion corridor analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 27% U-road RED condition documented</li>
                <li>✅ 118% pothole repair rise cited</li>
                <li>✅ HS2 diversion admission quoted</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Solihull</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=solihull"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Solihull claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Solihull has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Solihull matches its £7.162m capital allocation
                in 2025/26 and projects 81% preventative maintenance. But Section 58 turns on whether the
                specific defect that damaged your vehicle was reasonably inspected and repaired — not on
                aggregate budgets. The overall DfT rating is AMBER because road condition and best practice
                are both AMBER.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 680km — 69% of Solihull&apos;s 985km carriageway network. The
                council&apos;s own transparency report shows 27% of U-roads in RED condition in 2024, up from
                17% in 2021. The council admits HS2 diversion routes have accelerated deterioration on rural
                U-roads, where only &ldquo;make safe&rdquo; repairs could be undertaken until construction works finish.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council&apos;s claim that insurance claims have reduced weaken my case?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. Solihull states that its preventative approach &ldquo;has reduced&rdquo; insurance
                claims — a borough-wide trend, not proof that your specific defect was reasonably managed.
                Pothole repairs still rose from 753 in 2021/22 to 1,640 in 2024/25, and the council documents
                faster-than-expected deterioration on diversion routes. Your claim lives on evidence about
                your pothole, not borough-wide claim statistics.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the HS2 diversion admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Solihull publicly states that rural unclassified roads used as HS2 diversion routes have
                deteriorated faster than expected, that traffic management conflicts prevented major repairs,
                and that a &ldquo;make safe&rdquo; approach was required. That is documented knowledge of elevated risk
                on specific corridors — which raises the bar for what a reasonable inspection and repair regime
                looks like on affected routes under Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs more than doubled — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Solihull filled 1,640 potholes in 2024/25 compared with 753 in 2021/22 — a 118% rise.
                The council&apos;s own report describes a &ldquo;steady state&rdquo; on classified roads while U-road RED
                condition climbed back to 27%. Rising reactive repair counts on a network where 69% is
                unclassified roads is evidence that defects keep forming.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Who maintains the roads around Birmingham Airport and the NEC?
              </h3>
              <p className="text-surface-600 text-sm">
                Solihull Council maintains the borough&apos;s 985km local road network, including routes serving
                Birmingham Airport, Birmingham International Station and the NEC — all within the borough
                boundary. National Highways maintains trunk roads and motorways. HS2 Interchange Station will
                also sit within Solihull. Check who maintains your specific road before claiming; defects on
                trunk roads go to National Highways, not the borough council.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.solihull.gov.uk/roads-pavements-and-streetcare/highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Solihull Metropolitan Borough Council — Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
